import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Card, Chip, DChip, DataTable, EmptyState, Icon, Pane, PaneHead, PhoneCards, PhoneScreen, type TableRow,
} from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useMyDay, useReceivers, useTaskActions, type NurseTask, type Receiver } from './api';
import { numberWord, taskLabel } from './format';
import { byLane } from './dayModel';
import { NurseSearch } from './components/NurseSearch';
import { NurseMenu } from './components/NurseMenu';

const COLS = [
  { key: 'tick', label: '', width: 8 },
  { key: 'task', label: 'Task', width: 46 },
  { key: 'lane', label: 'Lane', width: 17 },
  { key: 'note', label: 'Note', width: 29 },
];
/* The canvas's page: three rows, then "showing 3 of 9 · Load more" — with
   room to spare below it, so this is the design's page size, not artboard
   space. Load more adds three at a time. */
const PAGE = 3;
const LANE_WORD = { rooms: 'live', prep: 'prep', after: 'after' } as const;

function Tick({ on }: { on: boolean }) {
  return on ? (
    <span aria-hidden style={{ width: 18, height: 18, borderRadius: 5, background: 'var(--brand-500)',
      color: 'var(--surface-0)', fontSize: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      ✓
    </span>
  ) : (
    <span aria-hidden style={{ width: 18, height: 18, borderRadius: 5, border: '1px solid var(--stroke-200)',
      display: 'inline-block' }} />
  );
}

function initials(r: Receiver) {
  return `${r.firstName?.[0] ?? ''}${r.lastName?.[0] ?? ''}`.toUpperCase();
}

/**
 * Nurse · Hand over — `g_hand` (pick them one by one) and `g_handall` (All).
 *
 * THE LANES BECOME CHECKBOXES, THEN THE PERSON. The same open tasks as My day,
 * picked instead of worked; the pane is the selection set and the colleagues
 * it can go to. A running clock travels with its task — the start and the
 * giver stay on the record (the backend keeps both).
 *
 * The receivers are DERIVED: everyone holding the Nurse role who opened MAIE
 * here today. Never a rota, and never a control someone keeps up to date.
 */
export function NurseHandOverPage({ all }: { all: boolean }) {
  const navigate = useNavigate();
  const day = useMyDay();
  const receivers = useReceivers();
  const { handOver } = useTaskActions();
  const [ticked, setTicked] = useState<string[]>([]);
  const [to, setTo] = useState<string | null>(null);
  const [shown, setShown] = useState(PAGE);
  const [searching, setSearching] = useState(false);
  usePageMeta({ title: 'Hand over' });

  const open = useMemo(
    () => (day.data?.tasks ?? [])
      .filter((t) => t.mine && (t.state === 'open' || t.state === 'in_progress'))
      .sort(byLane),
    [day.data],
  );
  const listed = open;
  const nothing = Boolean(day.data) && open.length === 0;
  const isTicked = (t: NurseTask) => all || ticked.includes(t.id);
  const count = all ? open.length : ticked.filter((id) => open.some((t) => t.id === id)).length;
  const people = receivers.data ?? [];

  const toggle = (id: string) => {
    if (all) return;
    setTicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const go = () => {
    if (!open.length) {
      toast.info('Nothing to hand over — every task on your day is finished or already with someone');
      return;
    }
    if (!count) {
      toast.error('Tick at least one task first');
      return;
    }
    if (!to) {
      toast.error('Pick who takes them — in the panel');
      return;
    }
    const name = people.find((p) => p.id === to);
    handOver.mutate(
      all ? { toUserId: to, all: true } : { toUserId: to, taskIds: ticked },
      {
        onSuccess: () => {
          toast.success(`Handed to ${name?.firstName ?? 'your colleague'} — she has been pinged`);
          navigate('/nurse/day');
        },
        onError: (e) => toast.error((e as Error).message || 'The hand-over did not go through'),
      },
    );
  };


  const rows: TableRow[] = listed.slice(0, shown).map((t) => ({
    id: t.id,
    cells: {
      tick: (
        <span role="checkbox" aria-checked={isTicked(t)} aria-label={`Tick ${taskLabel(t)}`}>
          <Tick on={isTicked(t)} />
        </span>
      ),
      task: <p className="t-strong">{taskLabel(t)}</p>,
      lane: <span className="t-sec mut">{LANE_WORD[t.lane]}</span>,
      note:
        t.state === 'in_progress' && t.dueAt ? (
          <span className="t-sec amb">⏱ running · the clock keeps running</span>
        ) : t.state === 'in_progress' ? (
          <span className="t-sec mut">in progress</span>
        ) : t.lane === 'rooms' ? (
          <span className="t-sec mut">not started</span>
        ) : (
          <span className="t-sec mut">—</span>
        ),
    },
  }));

  const recipientCards = people.map((p) => (
    <Card
      key={p.id}
      onClick={() => setTo(p.id)}
      style={to === p.id ? { borderColor: 'var(--brand-500)', background: 'var(--brand-100)' } : undefined}
    >
      <div className="flex" style={{ gap: 10, alignItems: 'center' }}>
        <span className="av av-sm" style={p.role === 'Nurse'
          ? { background: 'var(--brand-100)', color: 'var(--brand-600)' }
          : { background: 'var(--success-100)', color: 'var(--success-600)' }}>
          {initials(p)}
        </span>
        <div className="grow">
          <p className="t-strong">{p.firstName} {p.lastName}</p>
          <p className="t-cap mut">{p.role === 'Nurse' ? 'Nurse · here today' : `${p.role} · holds Nurse role`}</p>
        </div>
        <Icon name="chevron-right" className="mut" />
      </div>
    </Card>
  ));

  useShellChrome(
    {
      raw: true,
      title: 'My day · hand over',
      /* `g_n6` — pick the tasks, then the person. The tick is the card's whole
         gesture at this width: a 20px checkbox beside a 44px row is a target
         nobody hits, so tapping the card IS ticking it. */
      phone: (
        <PhoneScreen
          className="nurse-phone"
          crumb="My day"
          title="Hand over"
          onBack={() => navigate('/nurse/day')}
          menu={(close) => <NurseMenu door="day" onClose={close} />}
        >
          {open.length ? (
            <p className="t-sec mut" style={{ padding: '0 var(--m) 8px' }}>
              {all ? (
                <>
                  All {numberWord(open.length)} open tasks go together — every one is ticked.{' '}
                  <button type="button" className="lnk" onClick={() => navigate('/nurse/day/hand-over')}>Pick tasks instead</button>
                </>
              ) : (
                'Tap the tasks that go, then hand them over.'
              )}
            </p>
          ) : null}
          <PhoneCards
            columns={COLS}
            rows={rows}
            body="task"
            trailing="tick"
            hide={['lane']}
            onOpen={(r) => toggle(r.id)}
            empty={<EmptyState title="Nothing to hand over"
              sub="Only what is still open can be handed on — a task you have finished stays yours." />}
          />
          {count > 0 && people.length ? (
            <div className="card mt12" style={{ margin: '12px 0 0', borderColor: 'var(--brand-500)' }}>
              <p className="t-sec w6 mb8" style={{ color: 'var(--brand-600)' }}>
                Hand {count} task{count === 1 ? '' : 's'} to
              </p>
              {recipientCards}
              {to ? (
                <button type="button" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} onClick={go}>
                  Hand {count} task{count === 1 ? '' : 's'} ▸
                </button>
              ) : null}
            </div>
          ) : null}
          {listed.length > shown ? (
            <p className="mt12" style={{ padding: '0 var(--m)' }}>
              <button type="button" className="lnk" onClick={() => setShown((n) => n + PAGE * 5)}>
                Show more · {listed.length - shown} left
              </button>
            </p>
          ) : null}
        </PhoneScreen>
      ),
      sub: all ? (
        <>
          <button type="button" className="lnk" onClick={() => navigate('/nurse/day/hand-over')}>‹ Hand over</button>
          {' '}▸ All — every open task ticked
        </>
      ) : (
        <>
          <button type="button" className="lnk" onClick={() => navigate('/nurse/day')}>‹ My day</button>
          {' '}▸ hand over — tick the tasks, then the person
        </>
      ),
      search: 'Search my day…',
      onSearch: () => setSearching(true),
      /* Nothing open, nothing to hand: no primary, and the pane says so. */
      primary: nothing ? undefined
        : { label: all ? `Hand ${open.length} tasks ▸` : 'Hand the ticked tasks ▸', onClick: go },
      pane: nothing ? (
        <Pane head={<PaneHead overline="Hand-over · the selection set" title="Nothing to hand over"
          sub="no open task on your day" />}>
          <div className="emptystate">
            <p className="t-sec">
              Every task on your day is finished or already with someone else — there is nothing to hand on.
            </p>
          </div>
        </Pane>
      ) : (
        <Pane
          head={
            <PaneHead
              overline="Hand-over · the selection set"
              title={all ? `Hand ${open.length} tasks to` : 'Hand the ticked tasks to'}
              sub={all ? 'every open task is ticked'
                : count ? `${count} of the ${open.length} open tasks ticked` : 'nothing ticked yet — pick the tasks that go'}
            />
          }
        >
          {people.length ? (
            recipientCards
          ) : (
            <div className="emptystate">
              <p className="t-sec">
                {receivers.isLoading ? 'Looking for who is here…'
                  : 'Nobody else holding the Nurse role has opened MAIE here today — there is no one to hand to yet.'}
              </p>
            </div>
          )}
          <p className="t-cap mut center mt12">everyone PRESENT with the role at this branch · receiver pinged · logged</p>
          {all ? null : (
            <div className="card" style={{ marginTop: 8 }}>
              <p className="t-sec sec">Presence is derived — holds the role and opened MAIE here today. It is never a rota.</p>
            </div>
          )}
        </Pane>
      ),
    },
    [all, open.length, count, nothing, to, people.map((p) => p.id).join(','), receivers.isLoading],
  );

  return (
    <div className="maie desk nurse-root">
      <div className="filterrow">
        <span className="t-ovl mut" style={{ marginRight: 4 }}>Ticked</span>
        <Chip small kind={all ? 'outline' : 'brand'} className={all ? undefined : 'w6'}
          onClick={() => navigate('/nurse/day/hand-over')}>Pick them one by one</Chip>
        {/* A zero-count chip is not drawn — there is nothing to tick all of. */}
        {open.length ? (
          <Chip small kind={all ? 'brand' : 'outline'} className={all ? 'w6' : undefined}
            onClick={() => navigate('/nurse/day/hand-over/all')}>All · {open.length}</Chip>
        ) : null}
        <span className="grow" />
        <span className="t-cap mut">
          {all ? 'every open task, the running clock included' : 'a running clock travels with its task'}
        </span>
      </div>
      <div className="dcontent">
        {!day.data ? (
          <p className="t-sec mut">{day.isError ? 'Your day could not be loaded — it retries on its own.' : 'Loading your day…'}</p>
        ) : (
          <>
            <DataTable
              columns={COLS}
              rows={rows}
              onSelect={(r) => toggle(r.id)}
              selectedId={null}
              caption="Tasks to hand over"
              total={listed.length}
              onLoadMore={() => setShown((n) => n + PAGE)}
              empty={
                <div className="emptystate">
                  <p className="es-t">Nothing open to hand over.</p>
                  <p className="t-sec mut">Every task on your day is finished or already with someone else.</p>
                  <p className="mt12">
                    <DChip kind="outline" onClick={() => navigate('/nurse/day')}>Back to My day</DChip>
                  </p>
                </div>
              }
            />
            {open.length ? (
              <p className="t-sec mut mt12">
                {all
                  ? `All ${numberWord(open.length)} open tasks go together — the shift ends, the station does not.`
                  : 'The lanes become checkboxes — the same tasks, picked instead of worked.'}
              </p>
            ) : null}
          </>
        )}
      </div>
      <NurseSearch open={searching} onClose={() => setSearching(false)} />
    </div>
  );
}
